import { useState, type CSSProperties, type JSX } from "react";
import type { components } from "../../api/schema";
import {
  parseInlineMarkup,
  DEFAULT_COLOR,
  type CharacterEntry,
} from "./parseInlineMarkup";
import { useSegmentTypewriter } from "./useSegmentTypewriter";
import { resolveVariantThumbnailUrl } from "./characterAssetPaths";
import { resolveDialogueThumbnailVariantId } from "./resolveDialogueThumbnailVariant";
import { getContrastTextColor } from "./endingPalette";

type Segment = components["schemas"]["Segment"];

export type SegmentRendererProps = {
  segment: Segment;
  charactersMap: Map<string, CharacterEntry>;
  visualAssets?: components["schemas"]["VisualAssets"] | undefined;
  /** エンディング本文だけに適用する作者指定の文字色。話者表示色には適用しない。 */
  textColorOverride?: string | undefined;
  /** エンディング段落送りと二重に待たせないため、表示時に本文を全文描画する。 */
  disableTypewriter?: boolean | undefined;
  /** visual_novel では ADV 風の名前欄 + 本文表示に寄せる。 */
  variant?: "default" | "visualNovel";
  /** narration / dialogue の本文面だけに適用する scenario palette。 */
  panelBackgroundColor?: string | null | undefined;
  /** ending の台詞カード専用配色。指定時は話者名・本文を読みやすい対照色にする。 */
  dialogueBackgroundColor?: string | null | undefined;
  dialogueTextColor?: string | null | undefined;
  /** EndingView の話者アバター文字だけ、背景色設定にかかわらず話者色との対照を取る。 */
  ensureAvatarTextContrast?: boolean | undefined;
};

type SpeakerStyle = CSSProperties & {
  "--speaker-color": string;
};

const VISUAL_NOVEL_UNKNOWN_THUMBNAIL_FACE_CLASS =
  "h-full w-full origin-top -translate-y-3 scale-[1.12] object-cover object-top";
const VISUAL_NOVEL_UNKNOWN_BASE_FACE_CLASS =
  "h-full w-full origin-top -translate-y-8 scale-[4.2] object-cover object-top";
const VISUAL_NOVEL_DEFAULT_STILL_FACE_CLASS =
  "h-full w-full origin-[50%_18%] scale-[2.3] object-cover object-top";

const GLASS_HARBOUR_MIO_FACE_CLASS =
  "h-full w-full origin-[50%_26%] scale-[2.4] object-cover object-top";
const GLASS_HARBOUR_REI_FACE_CLASS =
  "h-full w-full origin-[50%_14%] scale-[2.0] object-cover object-top";
const GLASS_HARBOUR_KAREN_FACE_CLASS =
  "h-full w-full origin-[52%_19%] scale-[2.0] object-cover object-top";

const VISUAL_NOVEL_FACE_CROP_CLASS_BY_PATH: Record<string, string> = {
  "/portraits/last-candlestick/lady-eleanor/suspicious.png":
    "h-full w-full origin-top -translate-y-4 scale-[1.18] object-cover object-top",
  "/portraits/last-candlestick/lady-eleanor/neutral.png":
    "h-full w-full origin-top -translate-y-5 scale-[1.22] object-cover object-top",
  "/portraits/last-candlestick/lady-eleanor/confronted.png":
    "h-full w-full origin-top -translate-y-4 scale-[1.16] object-cover object-top",
  "/portraits/last-candlestick/lady-eleanor/shocked.png":
    "h-full w-full origin-top -translate-y-4 scale-[1.18] object-cover object-top",
  "/portraits/last-candlestick/butler-hawthorne/neutral.png":
    "h-full w-full origin-top -translate-y-3 scale-[1.14] object-cover object-top",
  "/portraits/last-candlestick/butler-hawthorne/suspicious.png":
    "h-full w-full origin-top -translate-y-3 scale-[1.14] object-cover object-top",
  "/portraits/last-candlestick/butler-hawthorne/confronted.png":
    "h-full w-full origin-top -translate-y-3 scale-[1.14] object-cover object-top",
  "/portraits/last-candlestick/butler-hawthorne/shocked.png":
    "h-full w-full origin-top -translate-y-3 scale-[1.14] object-cover object-top",
  "/portraits/last-candlestick/detective-ashford/neutral.png":
    "h-full w-full origin-top -translate-y-2 scale-[1.08] object-cover object-top",
  "/portraits/last-candlestick/detective-ashford/confronted.png":
    "h-full w-full origin-top -translate-y-2 scale-[1.08] object-cover object-top",
  "/portraits/last-candlestick/detective-ashford/shocked.png":
    "h-full w-full origin-top -translate-y-2 scale-[1.08] object-cover object-top",
  "/portraits/last-candlestick/detective-ashford/suspicious.png":
    "h-full w-full origin-top -translate-y-2 scale-[1.08] object-cover object-top",
  "/marion-stills/last-candlestick/lady-eleanor.png":
    "h-full w-full origin-top -translate-y-8 scale-[4.2] object-cover object-top",
  "/marion-stills/last-candlestick/lady-eleanor/confronted.png":
    "h-full w-full origin-top -translate-y-7 scale-[4.05] object-cover object-top",
  "/marion-stills/last-candlestick/lady-eleanor/shocked.png":
    "h-full w-full origin-top -translate-y-7 scale-[4.05] object-cover object-top",
  "/marion-stills/last-candlestick/butler-hawthorne.png":
    "h-full w-full origin-top -translate-y-10 scale-[4.2] object-cover object-top",
  "/marion-stills/last-candlestick/butler-hawthorne/suspicious.png":
    "h-full w-full origin-top -translate-y-8 scale-[4.05] object-cover object-top",
  "/marion-stills/last-candlestick/detective-ashford.png":
    "h-full w-full origin-top -translate-y-3 scale-[3.65] object-cover object-top",
  "/marion-stills/last-candlestick/detective-ashford/confronted.png":
    "h-full w-full origin-top -translate-y-5 scale-[3.85] object-cover object-top",
  "/marion-stills/glass-harbour/mio.png": GLASS_HARBOUR_MIO_FACE_CLASS,
  "/marion-stills/glass-harbour/mio_motion.png": GLASS_HARBOUR_MIO_FACE_CLASS,
  "/marion-stills/glass-harbour/rei.png": GLASS_HARBOUR_REI_FACE_CLASS,
  "/marion-stills/glass-harbour/karen.png": GLASS_HARBOUR_KAREN_FACE_CLASS,
};

// AIが「キャラ名「セリフ」」形式で生成した場合、ヘッダーと重複するため先頭の名前+括弧を除去する
function stripSpeakerPrefix(text: string, speakerName: string): string {
  // markup付き: {{char:...}}NAME{{/char}}「 → 「
  const markupStripped = text.replace(
    /^\{\{char:[^}]+\}\}[^{「『\n]+\{\{\/char\}\}([「『])/,
    "$1",
  );
  if (markupStripped !== text) return markupStripped;
  // plain name + 「 or 『
  if (
    speakerName &&
    (text.startsWith(speakerName + "「") || text.startsWith(speakerName + "『"))
  ) {
    return text.slice(speakerName.length);
  }
  return text;
}

function getAssetPathname(assetUrl: string): string {
  try {
    return new URL(assetUrl, "http://palimia.local").pathname;
  } catch {
    return assetUrl;
  }
}

function getVisualNovelFaceClassName(assetUrl: string, isUsingBase: boolean): string {
  const pathname = getAssetPathname(assetUrl);
  if (VISUAL_NOVEL_FACE_CROP_CLASS_BY_PATH[pathname]) {
    return VISUAL_NOVEL_FACE_CROP_CLASS_BY_PATH[pathname];
  }

  // 硝子港（glass-harbour）のバリアント画像（/mio/smile.png 等）にも適切な顔切り取りを適用
  if (pathname.includes("glass-harbour")) {
    if (/\/(?:mio)(?:[\/._]|$)/.test(pathname)) {
      return GLASS_HARBOUR_MIO_FACE_CLASS;
    }
    if (/\/(?:rei)(?:[\/._]|$)/.test(pathname)) {
      return GLASS_HARBOUR_REI_FACE_CLASS;
    }
    if (/\/(?:karen)(?:[\/._]|$)/.test(pathname)) {
      return GLASS_HARBOUR_KAREN_FACE_CLASS;
    }
  }

  if (pathname.includes("/marion-stills/")) {
    return VISUAL_NOVEL_DEFAULT_STILL_FACE_CLASS;
  }

  return (
    isUsingBase
      ? VISUAL_NOVEL_UNKNOWN_BASE_FACE_CLASS
      : VISUAL_NOVEL_UNKNOWN_THUMBNAIL_FACE_CLASS
  );
}

// narration / dialogue を視覚的に区別するため、discriminated union を type で分岐する。
// RTL からの判定を容易にするため、ルート要素に data-segment-type と data-testid を付与する。
export function SegmentRenderer({
  segment,
  charactersMap,
  visualAssets,
  textColorOverride,
  disableTypewriter = false,
  variant = "default",
  panelBackgroundColor,
  dialogueBackgroundColor,
  dialogueTextColor,
  ensureAvatarTextContrast = false,
}: SegmentRendererProps): JSX.Element {
  // dialogue 用フィールドを hooks より前に計算する（hooks は条件分岐より前に呼ぶ必要があるため）
  const speaker =
    segment.type === "dialogue"
      ? charactersMap.get(segment.speakerId)
      : undefined;
  const speakerColor = speaker?.color ?? DEFAULT_COLOR;
  const basePortraitUrl = speaker?.portraitUrl ?? null;
  const isVersioned = visualAssets?.mode === "versioned";
  const thumbnailVariantId =
    segment.type === "dialogue"
      ? (isVersioned ? segment.actingVariantId : resolveDialogueThumbnailVariantId(segment))
      : undefined;
  const variantPortraitUrl =
    segment.type === "dialogue"
      ? resolveVariantThumbnailUrl(basePortraitUrl, thumbnailVariantId, visualAssets, segment.speakerId)
      : null;
  const portraitUrl = isVersioned ? variantPortraitUrl : variantPortraitUrl ?? basePortraitUrl;
  const portraitFallbackUrl = !isVersioned && variantPortraitUrl !== null ? basePortraitUrl : null;
  // AI 生成シーンが speakerName に {{char:...}}名前{{/char}} タグを混入するケースへの防衛。
  // speakerName は dialogue variant にのみ存在するため union narrowing してから参照する。
  // 型上は required だが旧データ / fixture で欠落しうるため runtime fallback も残す。
  const rawSpeakerName =
    (segment.type === "dialogue" ? segment.speakerName : "") ?? "";
  const displayName = rawSpeakerName.replace(
    /\{\{(?:char:[^}]+|keyword)\}\}|\{\{\/(?:char|keyword)\}\}/g,
    "",
  );

  // dialogue では先頭の「名前「」を除去してからタイプライター処理する
  const rawText =
    segment.type === "dialogue"
      ? stripSpeakerPrefix(segment.text, displayName)
      : segment.text;

  // テキスト長に比例した表示速度（12ms/char、上限3s）
  const durationMs = Math.min(400 + rawText.length * 12, 3000);
  const typedText = useSegmentTypewriter(rawText, durationMs);
  const displayText = variant === "visualNovel" || disableTypewriter ? rawText : typedText;

  if (variant === "visualNovel") {
    if (segment.type === "narration") {
      return (
        <p
          data-testid="segment-narration"
          data-segment-type="narration"
          className="h-36 max-h-36 min-h-36 overflow-y-auto rounded-lg border border-white/25 bg-slate-950/94 px-4 py-4 text-pretty text-lg leading-relaxed text-slate-50 shadow-[0_18px_56px_rgba(0,0,0,0.62)] drop-shadow-[0_1px_1px_rgba(0,0,0,0.8)] backdrop-blur-md landscape:h-32 landscape:max-h-32 landscape:min-h-32 md:h-[9.5rem] md:max-h-[9.5rem] md:min-h-[9.5rem] md:px-6 md:py-5 md:text-xl"
          style={panelBackgroundColor ? { backgroundColor: panelBackgroundColor } : undefined}
        >
          {parseInlineMarkup(displayText, charactersMap, textColorOverride)}
        </p>
      );
    }

    return (
      <div
        data-testid="segment-dialogue"
        data-segment-type="dialogue"
        className="relative grid h-36 max-h-36 min-h-36 grid-cols-[9rem_minmax(0,1fr)] gap-2 landscape:h-32 landscape:max-h-32 landscape:min-h-32 landscape:grid-cols-[8rem_minmax(0,1fr)] md:h-[9.5rem] md:max-h-[9.5rem] md:min-h-[9.5rem] md:grid-cols-[9.5rem_minmax(0,1fr)] md:gap-3"
        style={{ "--speaker-color": speakerColor } as SpeakerStyle}
      >
        <p
          data-testid="segment-speaker-name"
          className="absolute -top-8 left-0 z-10 inline-flex max-w-[calc(100%-0.5rem)] truncate rounded-md border border-white/15 border-l-4 border-l-[var(--speaker-color)] bg-slate-950/88 px-3 py-1 text-xs font-semibold text-slate-50 shadow-[0_8px_20px_rgba(0,0,0,0.32)] backdrop-blur-md md:-top-9 md:text-sm"
          title={displayName}
        >
          {displayName}
        </p>
        <div
          data-testid="visual-novel-speaker-face"
          className="h-full w-full shrink-0 overflow-hidden rounded-md border border-white/20 bg-reader-page shadow-[0_14px_30px_rgba(0,0,0,0.34)]"
          aria-hidden="true"
        >
          {portraitUrl != null ? (
            <SpeakerFaceImage
              key={`${isVersioned ? "versioned" : "legacy"}:${portraitUrl}`}
              alt=""
              exactImageOnly={isVersioned}
              fallbackUrl={portraitFallbackUrl}
              primaryUrl={portraitUrl}
              variant={variantPortraitUrl !== null ? "thumbnail" : "base"}
              visualVariant="visualNovel"
            />
          ) : (
            <div
              className="flex h-full w-full items-center justify-center text-2xl font-bold text-white"
              style={{ backgroundColor: speakerColor }}
            >
              {(displayName || "?").charAt(0)}
            </div>
          )}
        </div>
        <div className="h-full min-w-0">
          <div
            data-testid="visual-novel-dialogue-panel"
            className="flex h-full items-start overflow-y-auto rounded-lg border border-white/25 bg-slate-950/94 py-4 pl-3 pr-4 text-slate-50 shadow-[0_18px_56px_rgba(0,0,0,0.62)] backdrop-blur-md md:py-5 md:pl-4 md:pr-6"
            style={panelBackgroundColor ? { backgroundColor: panelBackgroundColor } : undefined}
          >
            <p
              data-testid="segment-dialogue-text"
              className="min-w-0 text-pretty break-words text-lg leading-relaxed text-slate-50 drop-shadow-[0_1px_1px_rgba(0,0,0,0.8)] md:text-xl"
            >
              {parseInlineMarkup(displayText, charactersMap, textColorOverride)}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (segment.type === "narration") {
    return (
      <p
        data-testid="segment-narration"
        data-segment-type="narration"
        className="tracking-wide text-reader-secondary leading-relaxed"
        style={{
          fontSize: "var(--reader-font-base)",
          ...(panelBackgroundColor ? { backgroundColor: panelBackgroundColor } : {}),
          ...(panelBackgroundColor ? { borderRadius: "0.375rem", padding: "0.75rem" } : {}),
          ...(textColorOverride ? { color: textColorOverride } : {}),
        }}
      >
        {parseInlineMarkup(displayText, charactersMap, textColorOverride)}
      </p>
    );
  }

  return (
    <div
      data-testid="segment-dialogue"
      data-segment-type="dialogue"
      className="flex items-start gap-2.5"
    >
      {/* 顔画像 or カラーアバター */}
      <div className="shrink-0 mt-1" aria-hidden="true">
        {portraitUrl != null ? (
          <SpeakerFaceImage
            key={`${isVersioned ? "versioned" : "legacy"}:${portraitUrl}`}
            alt={segment.speakerName}
            exactImageOnly={isVersioned}
            borderColor={speakerColor}
            fallbackUrl={portraitFallbackUrl}
            primaryUrl={portraitUrl}
            variant={variantPortraitUrl !== null ? "thumbnail" : "base"}
            visualVariant="default"
          />
        ) : (
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white shrink-0"
            style={{
              backgroundColor: speakerColor,
              ...(ensureAvatarTextContrast ? { color: getContrastTextColor(speakerColor) } : {}),
            }}
          >
            {(displayName || "?").charAt(0)}
          </div>
        )}
      </div>

      {/* セリフ本文 */}
      <div
        data-testid={dialogueBackgroundColor ? "ending-dialogue-card" : undefined}
        className={`flex-1 border-l-4 border-[var(--speaker-color)] px-3 md:px-4 py-3 rounded ${dialogueBackgroundColor ? "" : "bg-reader-surface"}`}
        style={{
          "--speaker-color": speakerColor,
          ...(dialogueBackgroundColor ? { backgroundColor: dialogueBackgroundColor } : {}),
        } as SpeakerStyle}
      >
        <p
          className="text-sm font-semibold"
          style={{ color: dialogueTextColor ?? "var(--speaker-color)" }}
        >
          {displayName}
        </p>
        <p
          className="text-reader-primary leading-relaxed"
          style={{
            ...(dialogueTextColor || textColorOverride ? { color: dialogueTextColor ?? textColorOverride } : {}),
            ...(panelBackgroundColor ? { backgroundColor: panelBackgroundColor, borderRadius: "0.375rem", padding: "0.25rem 0.375rem" } : {}),
          }}
        >
          {parseInlineMarkup(displayText, charactersMap, textColorOverride)}
        </p>
      </div>
    </div>
  );
}

type SpeakerFaceImageProps = {
  alt: string;
  primaryUrl: string;
  fallbackUrl: string | null;
  variant: "thumbnail" | "base";
  visualVariant: "default" | "visualNovel";
  borderColor?: string | undefined;
  exactImageOnly: boolean;
};

function SpeakerFaceImage({
  alt,
  primaryUrl,
  fallbackUrl,
  variant,
  visualVariant,
  borderColor,
  exactImageOnly,
}: SpeakerFaceImageProps): JSX.Element | null {
  const [resolvedUrl, setResolvedUrl] = useState(primaryUrl);
  const [failed, setFailed] = useState(false);

  if (failed) return null;

  const isUsingBase = resolvedUrl === fallbackUrl || variant === "base";
  const className =
    exactImageOnly
      ? VISUAL_NOVEL_DEFAULT_STILL_FACE_CLASS
      : visualVariant === "visualNovel"
      ? getVisualNovelFaceClassName(resolvedUrl, isUsingBase)
      : "w-10 h-10 rounded-full object-cover object-top border-2";

  const image = (
    <img
      src={resolvedUrl}
      alt={alt}
      className={className}
      style={borderColor !== undefined ? { borderColor } : undefined}
      onError={() => {
        if (exactImageOnly) {
          setFailed(true);
          return;
        }
        if (
          fallbackUrl !== null &&
          fallbackUrl.length > 0 &&
          resolvedUrl !== fallbackUrl
        ) {
          setResolvedUrl(fallbackUrl);
        }
      }}
    />
  );
  // standing画像から顔を切り出し、別の未承認thumbnailを要求しない。
  return exactImageOnly && visualVariant === "default" ? (
    <div
      className="w-10 h-10 overflow-hidden rounded-full border-2"
      style={borderColor !== undefined ? { borderColor } : undefined}
    >
      {image}
    </div>
  ) : image;
}
